"use client";

import { useState } from "react";
import Image from "next/image";
import { Plus, Minus } from "lucide-react";
import AnimateOnScroll from "./AnimateOnScroll";
import CustomButton from "./CustomButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ThemeName, THEME_HEX, ICON_ACCENT_TEXT } from "./theme";

export interface FaqItem {
  q: string;
  a: string;
}

const defaultFaqs: FaqItem[] = [
  {
    q: "What should I expect from my first counselling session?",
    a: "Your first session is a chance to share what's brought you to counselling, at your own pace. The counsellor will listen without judgement, ask some gentle questions, and work with you to understand what kind of support would be most helpful going forward.",
  },
  {
    q: "How long does a counselling or coaching session last?",
    a: "Most sessions run for 50–60 minutes. If you need more time or have specific needs, this can be discussed when booking.",
  },
  {
    q: "Do you accept NDIS or Medicare clients?",
    a: "Yes. We work with both NDIS participants and clients eligible for Medicare-funded sessions. If you're unsure whether you qualify, contact us and we'll help you navigate your options.",
  },
  {
    q: "How do I know which service is right for me?",
    a: "A good starting point is our contact form — share a little about what you're going through and the team will recommend the most suitable service, whether that's counselling, life coaching, or allied health support.",
  },
];

interface FAQProps {
  theme?: Exclude<ThemeName, "white">;
  items?: FaqItem[];
}

const INITIAL_COUNT = 6;

const FAQ = ({ theme = "blue", items }: FAQProps) => {
  const faqs = items && items.length > 0 ? items : defaultFaqs;
  const [showAll, setShowAll] = useState(false);
  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_COUNT);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <section className="container bg-white" aria-labelledby="faq-heading">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14 3xl:gap-16">
        {/* Left: image */}
        <div className="relative aspect-[1242/1548] w-full shrink-0 self-start overflow-hidden rounded-[10px]">
          <Image
            src="/images/v3/home/faq-side-img.png"
            alt="Supportive counselling environment"
            fill
            className="object-cover"
            sizes="621px"
          />
        </div>

        {/* Right: header + accordion */}
        <div className="flex flex-1 flex-col gap-8">
          {/* Header */}
          <AnimateOnScroll className="flex flex-col gap-4">
            <span
              data-from-left
              className={`font-crimsonText text-[28px] font-bold`}
            >
              FAQ
            </span>
            <h2
              id="faq-heading"
              className={`heading ${theme === "blue" ? "text-v3-secondary" : "text-black"}`}
            >
              Helping You Start
              <br />
              With Confidence
            </h2>
            <p className="font-onest text-[18px] font-normal leading-[110%] text-black/70">
              Find clear answers about counselling, coaching, appointments, and
              what to expect before starting your support journey.
            </p>
            <div>
              <CustomButton
                label="View All FAQ"
                href="/counselling-faq"
                variant="primary"
                theme={theme}
              />
            </div>
          </AnimateOnScroll>

          {/* Accordion */}
          <Accordion type="single" collapsible className="w-full">
            {visibleFaqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b py-[6px]"
                style={{ borderColor: `${THEME_HEX[theme]}1A` }}
              >
                <AccordionTrigger className="group flex items-center justify-between py-2 text-left font-onest text-lg font-medium text-black hover:no-underline 3xl:py-[22px] 3xl:text-[20px] [&>svg:last-child]:hidden">
                  {faq.q}
                  <span className="shrink-0 text-black">
                    <Plus
                      size={20}
                      className="block group-data-[state=open]:hidden"
                    />
                    <Minus
                      size={20}
                      className="hidden group-data-[state=open]:block"
                    />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-[22px] font-onest text-[16px] font-normal leading-[150%] text-black/50">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {faqs.length > INITIAL_COUNT && (
            <div>
              <CustomButton
                label={showAll ? "Load less" : "Load more"}
                onClick={() => setShowAll((prev) => !prev)}
                variant="secondary"
                theme={theme}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
